import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, Users, Clock, ChefHat, Lightbulb, ShoppingBag,
  ChevronDown, Check, Star, Bookmark, BookmarkCheck,
  Utensils, Leaf,
} from 'lucide-react';
import { useAppStore } from '@/hooks/useAppStore';
import { ROUTE_PATHS, budgetLabel, mealTypeIcon, SideDish, Ingredient } from '@/lib/index';
import { springPresets } from '@/lib/motion';
import FlagRecipeButton from '@/components/FlagRecipeButton';
import { useSyncedRecipes } from '@/hooks/useSyncedRecipes';

// ── Rating helpers ────────────────────────────────────────────────────────────
const RATINGS_KEY = 'flyerfeast-ratings';
function getAllRatings(): Record<string, number> {
  try { return JSON.parse(localStorage.getItem(RATINGS_KEY) ?? '{}'); } catch { return {}; }
}
function saveMealRating(id: string, stars: number): void {
  const all = getAllRatings();
  if (stars === 0) { delete all[id]; } else { all[id] = stars; }
  localStorage.setItem(RATINGS_KEY, JSON.stringify(all));
}

// ── Star Rating ───────────────────────────────────────────────────────────────
function StarRatingWidget({ mealId }: { mealId: string }) {
  const [rating, setRating] = useState<number>(() => getAllRatings()[mealId] ?? 0);
  const [hover, setHover] = useState<number>(0);
  const [saved, setSaved] = useState(false);
  const handleRate = (stars: number) => {
    const next = rating === stars ? 0 : stars;
    setRating(next);
    saveMealRating(mealId, next);
    if (next > 0) { setSaved(true); setTimeout(() => setSaved(false), 1500); }
  };
  const active = hover || rating;
  return (
    <div className="flex flex-col items-start gap-1.5">
      <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Rate This Recipe</div>
      <div className="flex items-center gap-1.5">
        {[1, 2, 3, 4, 5].map(s => (
          <button key={s} onClick={() => handleRate(s)}
            onMouseEnter={() => setHover(s)} onMouseLeave={() => setHover(0)}
            className="transition-transform hover:scale-125 active:scale-110">
            <Star className={`w-7 h-7 transition-colors ${s <= active ? 'fill-yellow-400 text-yellow-400' : 'fill-none text-muted-foreground/40'}`} />
          </button>
        ))}
        {rating > 0 && <span className="text-sm text-muted-foreground ml-1 font-medium">{rating}.0</span>}
        <AnimatePresence>
          {saved && (
            <motion.span key="saved" initial={{ opacity: 0, x: 6 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }}
              className="text-xs text-primary font-semibold ml-1">✓ Saved</motion.span>
          )}
        </AnimatePresence>
      </div>
      {rating === 0 && <p className="text-[10px] text-muted-foreground">Tap a star to rate this recipe</p>}
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function qtyAlreadyInName(name: string, qty: string | undefined): boolean {
  if (!qty) return true;
  const n = name.toLowerCase();
  const q = qty.toLowerCase().replace(/\s+/g, '').replace(/g$/, 'g');
  const numMatch = qty.match(/[\d.]+/);
  if (numMatch && n.includes(numMatch[0])) return true;
  return n.includes(q.replace(/\s/g, ''));
}
function sp(val: unknown): number {
  if (typeof val === 'number') return isNaN(val) ? 0 : Math.max(0, val);
  const p = parseFloat(String(val ?? '').replace(/[$,\s]/g, ''));
  return isNaN(p) ? 0 : Math.max(0, p);
}
function normSide(s: string | SideDish): SideDish {
  if (typeof s === 'string') return { name: s, how: '' };
  return { name: s.name ?? '', how: s.how ?? '' };
}

// Pantry price estimates
const PANTRY_PRICES: Record<string, number> = {
  'flour': 0.30, 'oil': 0.25, 'olive oil': 0.35, 'salt': 0.05,
  'pepper': 0.08, 'black pepper': 0.08, 'sugar': 0.15, 'butter': 0.45,
  'garlic': 0.20, 'garlic powder': 0.10, 'onion powder': 0.10,
  'paprika': 0.12, 'cumin': 0.12, 'chili powder': 0.12,
  'oregano': 0.10, 'thyme': 0.10, 'rosemary': 0.10, 'basil': 0.10,
  'cinnamon': 0.10, 'cayenne': 0.10, 'turmeric': 0.12,
  'soy sauce': 0.20, 'vinegar': 0.15, 'baking soda': 0.05,
  'tomato paste': 0.35, 'broth': 0.50, 'chicken broth': 0.50,
  'beef broth': 0.50, 'vegetable broth': 0.50, 'cornstarch': 0.10,
  'honey': 0.30, 'mustard': 0.20, 'worcestershire': 0.20, 'hot sauce': 0.15,
};
function pantryPrice(name: string): number {
  const key = name.toLowerCase().trim();
  return Object.entries(PANTRY_PRICES).find(([k]) => key.includes(k))?.[1] ?? 0.25;
}

// Difficulty tag config — based on category or budget label
function difficultyTag(mealType: string, label: string): { text: string; cls: string } {
  const low = mealType?.toLowerCase();
  if (low?.includes('lazy') || label === 'Budget') return { text: 'Easy', cls: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400' };
  if (low?.includes('chef') || label === 'Premium') return { text: 'Weekend Project', cls: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-400' };
  return { text: 'Medium', cls: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-400' };
}

// ── Section Header (green, leaf icon) — matches competitor style ──────────────
function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2 mb-4">
      <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center shrink-0">
        <Leaf className="w-3.5 h-3.5 text-white" />
      </div>
      <h2 className="text-sm font-black uppercase tracking-widest text-primary">{children}</h2>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function RecipePage() {
  const { id } = useParams<{ id: string }>();
  const { mealPlan, cartMeals, togglePantryIngredient, selectedStore, savedRecipes } = useAppStore();
  const { saveRecipe, unsaveRecipe, isRecipeSaved } = useSyncedRecipes();
  const navigate = useNavigate();
  const [openSide, setOpenSide] = useState<number | null>(null);

  const baseMeal =
    mealPlan?.meals.find(m => m.id === id) ??
    cartMeals.find(m => m.id === id) ??
    savedRecipes.find(r => r.id === id) ??
    null;

  if (!baseMeal) {
    navigate(mealPlan ? ROUTE_PATHS.MEAL_PLAN : ROUTE_PATHS.SAVED_RECIPES);
    return null;
  }

  const cartVersion = cartMeals.find(m => m.id === id);
  const meal = cartVersion ?? baseMeal;
  const isInCart = !!cartVersion;

  const totalCost = meal.ingredients.reduce((s, i) => s + sp(i.price), 0);
  const serves = (meal.serves > 0 && meal.serves <= 12) ? meal.serves : 4;
  const perServing = totalCost / serves;
  const label = budgetLabel(perServing);

  const onSale = meal.ingredients.filter(i => i.on_sale);
  const fullPrice = meal.ingredients.filter(i => !i.on_sale && sp(i.price) > 0);
  const pantryBase: Ingredient[] = baseMeal.ingredients.filter(i => !i.on_sale && sp(i.price) === 0);
  const addedPantryNames = new Set(
    isInCart ? cartVersion!.ingredients.filter(i => !i.on_sale && sp(i.price) === 0).map(i => i.name) : []
  );

  const sides = (meal.sides ?? []).map(normSide).filter(s => s.name);
  const diff = difficultyTag(meal.meal_type, label);

  const handleTogglePantry = (ing: Ingredient) => {
    const { addToCart } = useAppStore.getState();
    if (!isInCart) addToCart(baseMeal);
    togglePantryIngredient(meal.id, ing);
  };

  const isFromSavedOnly = !mealPlan?.meals.find(m => m.id === id) && !cartMeals.find(m => m.id === id);
  const backPath = isFromSavedOnly ? ROUTE_PATHS.SAVED_RECIPES : ROUTE_PATHS.MEAL_PLAN;
  const saved = isRecipeSaved(meal.id);

  // Protein fallback image
  const saleIng = meal.ingredients?.find(i => i.on_sale)?.name ?? '';
  const searchText = `${saleIng} ${meal.name}`.toLowerCase();
  const PROTEIN_IMAGES: [RegExp, string][] = [
    [/salmon|trout|halibut|sablefish|arctic char/i, '/proteins/salmon_1.png'],
    [/shrimp|prawn|scallop|lobster|crab/i, '/proteins/shrimp_1.png'],
    [/cod|tilapia|haddock|pollock|basa|sole|snapper|mahi|pickerel|tuna|fish\b/i, '/proteins/fish_1.png'],
    [/ground beef|ground turkey|ground pork|mince/i, '/proteins/ground_beef_1.png'],
    [/steak|sirloin|rib.?eye|strip loin|tenderloin|brisket|short rib|beef roast|beef chuck/i, '/proteins/beef_1.png'],
    [/\bbeef\b|veal/i, '/proteins/beef_1.png'],
    [/chicken/i, '/proteins/chicken_1.png'],
    [/turkey/i, '/proteins/turkey_1.png'],
    [/lamb|mutton/i, '/proteins/lamb_1.png'],
    [/pork|ham\b|sausage|bratwurst|chorizo|ribs?/i, '/proteins/pork_1.png'],
  ];
  const fallbackImg = PROTEIN_IMAGES.find(([re]) => re.test(searchText))?.[1] ?? null;
  const heroSrc = meal.photo_url ?? fallbackImg;

  // All ingredients combined for the unified list
  const allIngredients = [...onSale, ...fullPrice, ...pantryBase];

  return (
    <div className="flex flex-col bg-background overflow-hidden" style={{ paddingTop: '48px', height: '100dvh' }}>

      {/* ── Sticky Header ── */}
      <header className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b border-border/50">
        <div className="container mx-auto px-4 h-14 flex items-center gap-3">
          <button onClick={() => navigate(backPath)}
            className="w-9 h-9 rounded-xl bg-muted hover:bg-muted/80 flex items-center justify-center transition-colors shrink-0">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <span className="font-bold text-foreground flex-1 truncate text-sm">{meal.name}</span>
          {/* Save */}
          <button onClick={() => saved ? unsaveRecipe(meal.id) : saveRecipe(meal, selectedStore?.name)}
            className={`w-9 h-9 rounded-xl flex items-center justify-center transition-all shrink-0 ${saved ? 'bg-primary text-white shadow-sm shadow-primary/30' : 'bg-muted text-muted-foreground hover:bg-primary/15 hover:text-primary'}`}
            title={saved ? 'Remove from My Recipes' : 'Save to My Recipes'}>
            {saved ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
          </button>
          {/* Flag */}
          <FlagRecipeButton recipeName={meal.name} storeName={selectedStore?.name} trigger="icon" />
          {/* Grocery list */}
          <button onClick={() => navigate(ROUTE_PATHS.GROCERY)}
            className="flex items-center gap-1.5 bg-primary text-white text-xs font-bold px-3 py-1.5 rounded-full hover:opacity-90 transition-opacity shrink-0">
            <ShoppingBag className="w-3.5 h-3.5" /> My List
          </button>
        </div>
      </header>

      {/* ── Scrollable content ── */}
      <div className="flex-1 overflow-y-auto overscroll-contain">
        <div className="container mx-auto max-w-xl px-4 py-5 pb-20 space-y-0">

          {/* ── Hero Photo ── */}
          {heroSrc && (
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              className="rounded-2xl overflow-hidden border border-border/30 shadow-sm mb-5"
              style={{ aspectRatio: '16/9' }}>
              <img src={heroSrc} alt={meal.name} className="w-full h-full object-cover"
                onError={e => { (e.target as HTMLImageElement).parentElement!.style.display = 'none'; }} />
            </motion.div>
          )}

          {/* ── RECIPE CARD ── white card, matches competitor format */}
          <motion.div
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            transition={springPresets.gentle}
            className="bg-card border border-border rounded-2xl overflow-hidden shadow-sm"
          >

            {/* ── Title section inside card ── */}
            <div className="px-5 pt-5 pb-4 border-b border-border/60">
              {/* Meal day / type label */}
              <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">
                {meal.day} · {mealTypeIcon(meal.meal_type)} {meal.meal_type}
              </p>
              {/* Title + difficulty tag */}
              <div className="flex items-start justify-between gap-3 mb-2">
                <h1 className="text-2xl font-black text-foreground leading-tight flex-1">{meal.name}</h1>
                <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full whitespace-nowrap shrink-0 mt-0.5 ${diff.cls}`}>
                  {diff.text}
                </span>
              </div>
              {/* Tagline */}
              {meal.tagline && (
                <p className="text-sm text-muted-foreground leading-relaxed">{meal.tagline}</p>
              )}
              {/* Quick meta pills: time / method / cuisine */}
              <div className="flex items-center gap-3 mt-3 flex-wrap">
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Clock className="w-3.5 h-3.5" />
                  <span>{meal.cook_time}</span>
                </div>
                {meal.method && (
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <ChefHat className="w-3.5 h-3.5" />
                    <span>{meal.method}</span>
                  </div>
                )}
                {/* Green cost/srv */}
                <div className="flex items-center gap-1 text-xs font-bold text-primary ml-auto">
                  <span>~${perServing.toFixed(2)}/srv</span>
                </div>
              </div>
            </div>

            {/* ── Summary Bar ── (Serves / Time / $/srv / Total) */}
            <div className="grid grid-cols-4 divide-x divide-border/60 bg-muted/30 border-b border-border/60">
              {[
                { icon: <Users className="w-3.5 h-3.5" />, top: `${serves}`, bot: 'Serves' },
                { icon: <Clock className="w-3.5 h-3.5" />, top: meal.cook_time, bot: 'Time' },
                { icon: null, top: `$${perServing.toFixed(2)}`, bot: 'Per Serving' },
                { icon: null, top: `$${totalCost.toFixed(2)}`, bot: 'Total Est.' },
              ].map((item, i) => (
                <div key={i} className="flex flex-col items-center justify-center py-3 px-1 gap-0.5">
                  {item.icon && <span className="text-primary">{item.icon}</span>}
                  <span className={`text-sm font-black ${i >= 2 ? 'text-primary' : 'text-foreground'}`}>{item.top}</span>
                  <span className="text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">{item.bot}</span>
                </div>
              ))}
            </div>

            {/* ── Nutrition (if available) ── */}
            {meal.nutrition && (
              <div className="px-5 pt-4 pb-1 border-b border-border/40">
                <div className="grid grid-cols-4 gap-2 mb-4">
                  {[
                    { label: 'Cal', value: meal.nutrition.calories, unit: 'kcal', icon: '🔥', color: 'bg-orange-500/10 border-orange-500/20 text-orange-500' },
                    { label: 'Protein', value: meal.nutrition.protein_g, unit: 'g', icon: '💪', color: 'bg-blue-500/10 border-blue-500/20 text-blue-500' },
                    { label: 'Carbs', value: meal.nutrition.carbs_g, unit: 'g', icon: '🌾', color: 'bg-yellow-500/10 border-yellow-500/20 text-yellow-600' },
                    { label: 'Fat', value: meal.nutrition.fat_g, unit: 'g', icon: '🫙', color: 'bg-purple-500/10 border-purple-500/20 text-purple-500' },
                  ].map(({ label, value, unit, icon, color }) => (
                    <div key={label} className={`flex flex-col items-center justify-center rounded-xl border p-2 gap-0.5 ${color}`}>
                      <span className="text-sm leading-none">{icon}</span>
                      <span className="text-base font-black leading-tight">{value}</span>
                      <span className="text-[8px] font-bold uppercase tracking-wider opacity-70">{unit}</span>
                      <span className="text-[8px] opacity-60">{label}</span>
                    </div>
                  ))}
                </div>
                <p className="text-[9px] text-muted-foreground text-center pb-3">AI nutrition estimate · per serving</p>
              </div>
            )}

            {/* ── INGREDIENTS section ── */}
            <div className="px-5 pt-5 pb-4 border-b border-border/60">
              <SectionHeader>Ingredients</SectionHeader>

              <div className="space-y-0 rounded-xl border border-border overflow-hidden">
                {/* On-sale items */}
                {onSale.map((ing, i) => (
                  <div key={`sale-${i}`}
                    className="flex items-center justify-between px-4 py-3 border-b border-border/40 last:border-0 bg-primary/5">
                    <div className="flex items-center gap-2 min-w-0 flex-1 pr-2">
                      <span className="text-[9px] font-black bg-primary text-white px-1.5 py-0.5 rounded-full shrink-0 leading-none">SALE</span>
                      <div className="min-w-0">
                        <span className="text-sm font-bold text-foreground">{ing.name}</span>
                        {ing.qty && !qtyAlreadyInName(ing.name, ing.qty) &&
                          <span className="text-xs text-muted-foreground ml-1.5 font-normal">{ing.qty}</span>}
                      </div>
                    </div>
                    <span className="text-sm font-black text-primary tabular-nums shrink-0">
                      {sp(ing.price) === 0
                        ? <span className="text-muted-foreground text-xs font-medium">pantry</span>
                        : `~$${sp(ing.price).toFixed(2)}`}
                    </span>
                  </div>
                ))}

                {/* Full-price items */}
                {fullPrice.map((ing, i) => (
                  <div key={`fp-${i}`}
                    className="flex items-center justify-between px-4 py-3 border-b border-border/40 last:border-0 hover:bg-muted/20 transition-colors">
                    <div className="min-w-0 flex-1 pr-2">
                      <span className="text-sm font-semibold text-foreground">{ing.name}</span>
                      {ing.qty && !qtyAlreadyInName(ing.name, ing.qty) &&
                        <span className="text-xs text-muted-foreground ml-1.5 font-normal">{ing.qty}</span>}
                    </div>
                    <span className="text-sm font-bold text-muted-foreground tabular-nums shrink-0">
                      ~${sp(ing.price).toFixed(2)}
                    </span>
                  </div>
                ))}

                {/* Pantry staples */}
                {pantryBase.map((ing, i) => {
                  const added = addedPantryNames.has(ing.name);
                  const estPrice = pantryPrice(ing.name);
                  return (
                    <button key={`pantry-${i}`} onClick={() => handleTogglePantry(ing)}
                      className={`w-full flex items-center gap-3 px-4 py-3 border-b border-border/30 last:border-0 text-left transition-colors ${added ? 'bg-primary/8' : 'hover:bg-muted/30'}`}>
                      <div className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${added ? 'bg-primary border-primary' : 'border-border bg-background'}`}>
                        {added && <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className={`text-sm ${added ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>{ing.name}</span>
                        {ing.qty && !qtyAlreadyInName(ing.name, ing.qty) &&
                          <span className="text-xs text-muted-foreground ml-1.5 font-normal">{ing.qty}</span>}
                      </div>
                      <span className={`text-xs font-semibold tabular-nums shrink-0 ${added ? 'text-primary' : 'text-muted-foreground/50'}`}>
                        ~${estPrice.toFixed(2)}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* Pantry helpers */}
              {pantryBase.length > 1 && (
                <div className="flex justify-between mt-2 px-0.5">
                  <button onClick={() => pantryBase.forEach(ing => { if (addedPantryNames.has(ing.name)) handleTogglePantry(ing); })}
                    className="text-xs text-muted-foreground font-medium hover:underline">Uncheck all</button>
                  <button onClick={() => pantryBase.forEach(ing => { if (!addedPantryNames.has(ing.name)) handleTogglePantry(ing); })}
                    className="text-xs text-primary font-semibold hover:underline">+ Add all pantry items</button>
                </div>
              )}

              {/* Cost total row */}
              <div className="flex items-center justify-between mt-3 bg-muted/30 rounded-xl px-4 py-2.5">
                <span className="text-xs font-semibold text-muted-foreground">Total Estimated Cost</span>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-muted-foreground">${perServing.toFixed(2)}/srv</span>
                  <span className="text-sm font-black text-foreground">~${totalCost.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* ── INSTRUCTIONS section ── */}
            <div className="px-5 pt-5 pb-5">
              <SectionHeader>Instructions</SectionHeader>

              <div className="space-y-4">
                {meal.instructions.map((step, i) => (
                  <motion.div key={i}
                    initial={{ opacity: 0, x: -8 }} whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                    className="flex gap-4"
                  >
                    {/* Green numbered circle */}
                    <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center shrink-0 shadow-sm shadow-primary/30 mt-0.5">
                      <span className="text-[13px] font-black text-white leading-none">{i + 1}</span>
                    </div>
                    <p className="text-sm text-foreground leading-relaxed pt-1.5 flex-1">{step}</p>
                  </motion.div>
                ))}
              </div>
            </div>

          </motion.div>{/* end recipe card */}

          {/* ── Chef's Tip ── */}
          {meal.tip && (
            <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
              className="flex items-start gap-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/60 rounded-2xl p-4 mt-4">
              <Lightbulb className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
              <div>
                <div className="text-xs font-bold text-amber-700 dark:text-amber-400 uppercase tracking-wider mb-1">Chef's Tip</div>
                <p className="text-sm text-amber-800 dark:text-amber-300 leading-relaxed">{meal.tip}</p>
              </div>
            </motion.div>
          )}

          {/* ── Star Rating ── */}
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
            className="bg-card border border-border rounded-2xl p-4 mt-4">
            <StarRatingWidget mealId={meal.id} />
          </motion.div>

          {/* ── Suggested Sides ── */}
          {sides.length > 0 && (
            <div className="mt-4">
              <div className="flex items-center gap-2 mb-3">
                <Utensils className="w-3.5 h-3.5 text-primary" />
                <h2 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Suggested Sides</h2>
              </div>
              <div className="space-y-2">
                {sides.map((side, i) => {
                  const hasHow = side.how && side.how.trim().length > 0;
                  const isOpen = openSide === i;
                  return (
                    <div key={i} className="bg-card border border-border rounded-2xl overflow-hidden">
                      <button
                        onClick={() => hasHow && setOpenSide(isOpen ? null : i)}
                        className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${hasHow ? 'hover:bg-muted/30 cursor-pointer' : 'cursor-default'}`}>
                        <div className="w-6 h-6 rounded-full bg-primary/10 text-primary text-[10px] font-bold flex items-center justify-center shrink-0">
                          {i + 1}
                        </div>
                        <span className="text-sm font-semibold text-foreground flex-1">{side.name}</span>
                        {hasHow && (
                          <motion.div animate={{ rotate: isOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>
                            <ChevronDown className="w-4 h-4 text-muted-foreground" />
                          </motion.div>
                        )}
                        {!hasHow && <span className="text-xs text-muted-foreground">suggested</span>}
                      </button>
                      <AnimatePresence>
                        {hasHow && isOpen && (
                          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }} className="overflow-hidden">
                            <div className="px-4 pb-4 pt-0">
                              <div className="bg-primary/5 border border-primary/15 rounded-xl p-3">
                                <div className="text-[10px] font-bold text-primary uppercase tracking-wider mb-1.5 flex items-center gap-1">
                                  <ChefHat className="w-3 h-3" /> How to Cook
                                </div>
                                <p className="text-xs text-foreground leading-relaxed">{side.how}</p>
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
